"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Globe, Menu, X, Sun, Moon } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";

import { useTheme } from "@/components/layout/ThemeProvider";
import { getSiteContent } from "@/content/site";
import { brandLogo } from "@/lib/assets";
import { track } from "@/lib/analytics";
import type { Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/provider";
import { newsNavLabel } from "@/lib/news/news-locale-redirect.mjs";
import { localePrefixFromPathname, localizePath, swapLocalePath } from "@/lib/i18n/routing";
import { useStoreWaitlist } from "@/components/landing/StoreWaitlistProvider";
import { LocalePickerList } from "@/components/layout/LocalePickerList";
import { detectPreferredStore } from "@/lib/store-urls";
import { LOCALE_DISPLAY_NAMES } from "@/lib/i18n/config";

export default function Navbar() {
  const { locale, setLocale } = useLocale();
  const { nav, common } = getSiteContent(locale);
  const { theme, toggle } = useTheme();
  const pathname = usePathname();
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPath, setMenuPath] = useState(pathname);
  const [desktopLangOpen, setDesktopLangOpen] = useState(false);
  const [mobileLangOpen, setMobileLangOpen] = useState(false);
  const [mobileSheetLangOpen, setMobileSheetLangOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const desktopLangRef = useRef<HTMLDivElement>(null);
  const mobileLangRef = useRef<HTMLDivElement>(null);
  const { shouldIntercept, interceptStoreClick } = useStoreWaitlist();

  const homeHref = localizePath("/", locale);
  const onHome = pathname === homeHref || pathname === "/";
  const showNavCta = !onHome || scrolled;
  const primaryCta = nav.ctaPrimary;
  const primaryHref = primaryCta.href;
  const ctaHref = localizePath(primaryHref, locale);
  const ctaLabel = primaryCta.label;
  const pathLocale = localePrefixFromPathname(pathname);

  // Sync from the URL only when the path actually encodes a locale (`/es`).
  // Unprefixed routes keep the picker / localStorage locale (fr, pt-BR, …).
  useEffect(() => {
    if (pathLocale) setLocale(pathLocale);
  }, [pathLocale, setLocale]);

  const switchLocale = (next: Locale) => {
    const target = swapLocalePath(pathname, next);
    setLocale(next);
    if (target !== pathname) {
      router.push(target);
    }
    setDesktopLangOpen(false);
    setMobileLangOpen(false);
    setMobileSheetLangOpen(false);
    setMenuOpen(false);
  };

  const isMenuOpen = menuOpen && menuPath === pathname;
  const compactHeader = scrolled && !isMenuOpen;
  const detachedHeader = scrolled && !isMenuOpen;
  const isDesktopLangOpen = desktopLangOpen && !compactHeader;
  const isMobileLangOpen = mobileLangOpen && !compactHeader;
  const navLogoSrc = theme === "light" ? brandLogo.layout01Dark : brandLogo.layout01Light;

  const closeMenu = () => {
    setMenuOpen(false);
    setMobileSheetLangOpen(false);
  };
  const toggleMenu = () => {
    if (isMenuOpen) {
      closeMenu();
      return;
    }
    setMenuPath(pathname);
    setMobileLangOpen(false);
    setMobileSheetLangOpen(false);
    setMenuOpen(true);
  };

  const handlePrimaryCtaClick = (
    event: ReactMouseEvent<HTMLAnchorElement>,
    entryPoint: string,
    shouldCloseMenu = false,
  ) => {
    track("cta_clicked", { cta: entryPoint });
    const store = detectPreferredStore();
    if (store && shouldIntercept(store)) {
      interceptStoreClick(event, `${entryPoint}_${store}`, store);
    }
    if (shouldCloseMenu) closeMenu();
    setDesktopLangOpen(false);
    setMobileLangOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  useEffect(() => {
    if (!isDesktopLangOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!desktopLangRef.current?.contains(e.target as Node)) setDesktopLangOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDesktopLangOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isDesktopLangOpen]);

  useEffect(() => {
    if (!isMobileLangOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!mobileLangRef.current?.contains(e.target as Node)) setMobileLangOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileLangOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [isMobileLangOpen]);

  const localeLabel = LOCALE_DISPLAY_NAMES[locale]?.code ?? "EN";
  const localeName = LOCALE_DISPLAY_NAMES[locale]?.nativeName ?? localeLabel;

  return (
    <header
      className={`fixed inset-x-0 transition-[top,padding] duration-300 ${
        isMenuOpen ? "z-[60]" : "z-50"
      } ${
        detachedHeader ? "top-3 px-3 md:top-4 md:px-5 lg:px-6" : "top-0 px-0"
      }`}
      aria-label={nav.ariaLabel}
    >
      <div
        className={`relative mx-auto w-full border transition-[max-width,border-radius,border-color,box-shadow,background-color,backdrop-filter] duration-300 ${
          detachedHeader
            ? "max-w-[1360px] rounded-[28px] border-[color:var(--color-surface-border)] shadow-[var(--shadow-elevated)] backdrop-blur-xl"
            : "max-w-none rounded-none border-transparent bg-transparent shadow-none backdrop-blur-none"
        }`}
        style={detachedHeader ? { backgroundColor: "var(--navbar-scrolled-bg)" } : undefined}
      >
        <nav
          className={`relative mx-auto flex max-w-[1280px] items-center justify-between px-6 transition-[height] duration-300 md:px-8 lg:px-10 ${
            compactHeader ? "h-[58px] md:h-[60px]" : "h-16 md:h-[72px]"
          }`}
        >
        <Link
          href={homeHref}
          aria-label={nav.brandLabel}
          className="flex items-center gap-3 rounded-full focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-accent-base)]"
        >
          <span className="md:hidden">
            <Image
              src={navLogoSrc}
              alt={nav.brandLabel}
              width={116}
              height={38}
              className={`w-auto transition-[height] duration-300 ${compactHeader ? "h-7" : "h-8"}`}
              priority
            />
          </span>
          <span className="hidden md:inline-flex">
            <Image
              src={navLogoSrc}
              alt={nav.brandLabel}
              width={158}
              height={52}
              className={`w-auto transition-[height] duration-300 ${compactHeader ? "h-8 lg:h-9" : "h-9 lg:h-10"}`}
              priority
            />
          </span>
        </Link>

        {!compactHeader && (
          <ul className="hidden items-center gap-1 md:flex">
            {nav.links.map((link) => {
              const isActive = pathname === "/" && link.href.startsWith("/#");
              return (
                <li key={link.href}>
                  <a
                    href={localizePath(link.href, locale)}
                    className={`relative inline-flex h-9 items-center rounded-full px-3 text-[13px] font-medium tracking-tight transition-colors ${
                      isActive
                        ? "text-[var(--color-text-primary)]"
                        : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                    }`}
                  >
                    {link.label}
                    {isActive && (
                      <span
                        aria-hidden="true"
                        className="absolute inset-x-3 bottom-1 h-px bg-[var(--color-accent-base)]"
                        style={{ opacity: 1 }}
                      />
                    )}
                  </a>
                </li>
              );
            })}
            <li>
              <Link
                href={localizePath("/news", locale)}
                className="relative inline-flex h-9 items-center rounded-full px-3 text-[13px] font-medium tracking-tight text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
              >
                {newsNavLabel(locale)}
              </Link>
            </li>
          </ul>
        )}

        <div className="hidden items-center gap-2 md:flex">
          {!compactHeader && (
            <div ref={desktopLangRef} className="relative">
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={isDesktopLangOpen}
                aria-controls="navbar-desktop-language-menu"
                aria-label={nav.changeLanguageLabel}
                onClick={() => setDesktopLangOpen((v) => !v)}
                className="inline-flex h-9 items-center gap-1.5 rounded-full border border-transparent px-3 text-[13px] font-medium tracking-tight transition-all text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[color:var(--color-surface-border)] hover:bg-[color:var(--color-surface-glass)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-base)]"
              >
                <Globe size={14} aria-hidden="true" />
                <span className="font-semibold">{localeLabel}</span>
              </button>
              <AnimatePresence>
                {isDesktopLangOpen && (
                  <motion.div
                    id="navbar-desktop-language-menu"
                    role="menu"
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.14 }}
                    className="absolute right-0 top-11 min-w-[16rem] rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-bg-raised)] p-1.5 shadow-[var(--shadow-elevated)]"
                  >
                    <LocalePickerList locale={locale} onSelect={switchLocale} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {showNavCta ? (
          <a
            href={ctaHref}
            onClick={(event) => handlePrimaryCtaClick(event, "navbar_primary")}
            className="inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-tight transition-colors duration-200 motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 h-9 px-4 text-sm bg-[var(--color-button-primary-bg)] text-[var(--color-button-primary-text)] hover:bg-[var(--color-button-primary-pressed)] focus-visible:outline-[var(--color-text-primary)]"
          >
            {ctaLabel}
          </a>
          ) : null}

          {!compactHeader && (
            <button
              type="button"
              onClick={toggle}
              aria-label={theme === "dark" ? common.themeLightLabel : common.themeDarkLabel}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-transparent text-[var(--color-text-secondary)] transition-all hover:border-[color:var(--color-surface-border)] hover:bg-[color:var(--color-surface-glass)] hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-base)]"
            >
              <AnimatePresence mode="wait" initial={false}>
                {theme === "dark" ? (
                  <motion.span
                    key="sun"
                    initial={{ rotate: -45, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 45, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Sun size={16} aria-hidden="true" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="moon"
                    initial={{ rotate: 45, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -45, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Moon size={16} aria-hidden="true" />
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 md:hidden">
          {!compactHeader && (
              <div ref={mobileLangRef} className="relative">
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={isMobileLangOpen}
                  aria-controls="navbar-mobile-language-menu"
                  aria-label={nav.changeLanguageLabel}
                  onClick={() => setMobileLangOpen((v) => !v)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full border border-transparent px-2.5 text-[13px] font-medium tracking-tight transition-all text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:border-[color:var(--color-surface-border)] hover:bg-[color:var(--color-surface-glass)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-base)]"
                >
                  <Globe size={14} aria-hidden="true" />
                  <span className="font-semibold">{localeLabel}</span>
                </button>
                <AnimatePresence>
                  {isMobileLangOpen && (
                    <motion.div
                      id="navbar-mobile-language-menu"
                      role="menu"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.14 }}
                      className="absolute right-0 top-11 min-w-[16rem] rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-bg-raised)] p-1.5 shadow-[var(--shadow-elevated)]"
                    >
                      <LocalePickerList locale={locale} onSelect={switchLocale} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
          )}

          {showNavCta ? (
          <a
            href={ctaHref}
            onClick={(event) =>
              handlePrimaryCtaClick(
                event,
                compactHeader ? "navbar_compact_primary" : "navbar_mobile_primary",
              )
            }
            className="inline-flex h-9 items-center justify-center gap-2 rounded-full px-3.5 text-[13px] font-semibold tracking-tight text-[var(--color-button-primary-text)] bg-[var(--color-button-primary-bg)]"
          >
            {ctaLabel}
          </a>
          ) : null}

          <button
            type="button"
            onClick={toggleMenu}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-base)]"
            aria-label={isMenuOpen ? nav.mobileCloseLabel : nav.mobileOpenLabel}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-nav-sheet"
          >
            <AnimatePresence mode="wait" initial={false}>
              {isMenuOpen ? (
                <motion.span
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <X size={22} aria-hidden="true" />
                </motion.span>
              ) : (
                <motion.span
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Menu size={22} aria-hidden="true" />
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
        </nav>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            id="mobile-nav-sheet"
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex h-dvh max-h-dvh flex-col overflow-hidden backdrop-blur-2xl md:hidden"
            style={{ backgroundColor: "var(--mobile-menu-bg)" }}
          >
            {/* Menu overlay header: logo + close button */}
            <div className="flex h-16 shrink-0 items-center justify-between border-b border-[var(--color-surface-border)] px-6">
              <Link
                href="/"
                aria-label={nav.brandLabel}
                onClick={closeMenu}
                className="flex items-center"
              >
                <Image
                  src={navLogoSrc}
                  alt={nav.brandLabel}
                  width={116}
                  height={38}
                  className="h-8 w-auto"
                  priority
                />
              </Link>
              <motion.button
                type="button"
                onClick={closeMenu}
                aria-label={nav.mobileCloseLabel}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                transition={{ duration: 0.18 }}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-surface-border-strong)] hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-base)]"
              >
                <X size={20} aria-hidden="true" />
              </motion.button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-contain px-6 py-4 pb-10">
              <ul className="flex flex-col gap-1">
                {nav.links.map((link, index) => (
                  <motion.li
                    key={link.href}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * index + 0.06, duration: 0.24 }}
                  >
                    <a
                      href={localizePath(link.href, locale)}
                      onClick={closeMenu}
                      className="block py-3 text-2xl font-semibold tracking-tight text-[var(--color-text-primary)] hover:text-[var(--color-accent-base)]"
                    >
                      {link.label}
                    </a>
                  </motion.li>
                ))}
                <motion.li
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * nav.links.length + 0.06, duration: 0.24 }}
                >
                  <Link
                    href={localizePath("/news", locale)}
                    onClick={closeMenu}
                    className="block py-3 text-2xl font-semibold tracking-tight text-[var(--color-text-primary)] hover:text-[var(--color-accent-base)]"
                  >
                    {newsNavLabel(locale)}
                  </Link>
                </motion.li>
              </ul>

              <div className="flex flex-col gap-2 border-t border-[var(--color-surface-border)] pt-4">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                  {common.languageLabel}
                </span>
                <button
                  type="button"
                  aria-expanded={mobileSheetLangOpen}
                  aria-controls="mobile-sheet-language-menu"
                  onClick={() => setMobileSheetLangOpen((open) => !open)}
                  className="flex h-12 w-full items-center justify-between rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] px-3 text-left text-sm text-[var(--color-text-primary)]"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <Globe size={16} aria-hidden="true" />
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                      {localeLabel}
                    </span>
                    <span className="truncate">{localeName}</span>
                  </span>
                  <ChevronDown
                    size={16}
                    aria-hidden="true"
                    className={`shrink-0 text-[var(--color-text-muted)] transition-transform ${
                      mobileSheetLangOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {mobileSheetLangOpen ? (
                  <div
                    id="mobile-sheet-language-menu"
                    role="group"
                    aria-label={common.selectLanguageGroupLabel}
                    className="rounded-2xl border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] p-1.5"
                  >
                    <LocalePickerList
                      locale={locale}
                      onSelect={switchLocale}
                      className="max-h-64"
                    />
                  </div>
                ) : null}

                <button
                  type="button"
                  onClick={toggle}
                  aria-label={theme === "dark" ? common.themeLightLabel : common.themeDarkLabel}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent-base)]"
                >
                  {theme === "dark" ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
                </button>

                {showNavCta ? (
                  <a
                    href={ctaHref}
                    onClick={(event) => handlePrimaryCtaClick(event, "navbar_menu_primary", true)}
                    className="inline-flex items-center justify-center gap-2 rounded-full font-semibold tracking-tight transition-all duration-200 motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-offset-2 h-14 px-7 text-base bg-[var(--color-button-primary-bg)] text-[var(--color-button-primary-text)] hover:bg-[var(--color-button-primary-pressed)] focus-visible:outline-[var(--color-text-primary)]"
                  >
                    {ctaLabel}
                  </a>
                ) : null}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
